import { useScoresProfils } from '../../api/profils.js';
import { formatDate } from '@artosera/shared';
import { Badge, Button, IconButton, Checkbox, Select } from '@artosera/ui';
import { ORIGINES_CONTACT, type TypeContact } from '@artosera/shared';
import { ChevronLeft, ChevronRight, Mail, Phone, Plus, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useContacts, type ContactPresente, type FiltresContacts } from '../../api/contacts.js';
import { useDemandes } from '../../api/demandes.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useOptionsTypeContact } from '../../api/typesContact.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { DemandesSegment } from './DemandesSegment.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { NouveauContactDialog } from './NouveauContactDialog.js';
import { BasculeFiltres, filtresRepliables } from '../filtres/BasculeFiltres.js';
import { useVue } from '../../preferences/vueStorage.js';
import { BasculeVue } from '../../composants/BasculeVue.js';
import styles from './ContactsPage.module.css';

const TRIS = ['nom', 'recent', 'ancien'] as const;

type Segment = 'carnet' | 'demandes';

/**
 * Contacts (3.3, écran 3, S3-c) : le carnet — liste dense, recherche par
 * fragment, filtres, tri, pagination — et, dans le même écran, le segment
 * « Demandes » avec son compteur « à traiter » (3.2, tableau de bord).
 */
export function ContactsPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'contacts', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { galerieSlug, galerie, compte } = useShellContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const segment: Segment = searchParams.get('onglet') === 'demandes' ? 'demandes' : 'carnet';
  function definirSegment(valeur: Segment): void {
    const suivants = new URLSearchParams(searchParams);
    if (valeur === 'carnet') suivants.delete('onglet');
    else suivants.set('onglet', valeur);
    setSearchParams(suivants, { replace: true });
  }

  const voitProfils =
    galerieAModule(galerie, 'profils-collectionneurs') && galerieALaction(galerie, 'profils:read');
  const [scoreMin, setScoreMin] = useState('');
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [type, setType] = useState('');
  const [etiquette, setEtiquette] = useState('');
  const [origine, setOrigine] = useState('');
  const [consentement, setConsentement] = useState('');
  const [inclureArchives, setInclureArchives] = useState(false);
  const [tri, setTri] = useState<FiltresContacts['tri']>('nom');
  const [page, setPage] = useState(1);
  const [nouveauOuvert, setNouveauOuvert] = useState(false);
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const filtresActifs = [
    type,
    etiquette,
    origine,
    consentement,
    voitProfils ? scoreMin : '',
    inclureArchives ? 'oui' : '',
  ].filter(Boolean).length;

  const filtres: FiltresContacts = useMemo(
    () => ({
      ...(rechercheDebattue ? { q: rechercheDebattue } : {}),
      ...(type ? { type: [type as TypeContact] } : {}),
      tri: !voitProfils && tri === 'score' ? 'nom' : tri,
      ...(voitProfils &&
      scoreMin !== '' &&
      Number.isInteger(Number(scoreMin)) &&
      Number(scoreMin) >= 0 &&
      Number(scoreMin) <= 100
        ? { scoreMin: Number(scoreMin) }
        : {}),
      page,
      parPage: 50,
      inclureArchives,
      ...(etiquette ? { etiquette } : {}),
      ...(origine ? { origine: origine as FiltresContacts['origine'] } : {}),
      ...(consentement ? { consentement: consentement === 'true' } : {}),
    }),
    [
      rechercheDebattue,
      type,
      etiquette,
      origine,
      consentement,
      inclureArchives,
      tri,
      page,
      scoreMin,
      voitProfils,
    ],
  );

  const cleFiltresSansPage = JSON.stringify([
    rechercheDebattue,
    type,
    etiquette,
    origine,
    consentement,
    inclureArchives,
    voitProfils,
    voitProfils ? scoreMin : '',
    voitProfils && tri === 'score',
  ]);
  const [cleFiltresPrecedente, setCleFiltresPrecedente] = useState(cleFiltresSansPage);
  if (cleFiltresSansPage !== cleFiltresPrecedente) {
    setCleFiltresPrecedente(cleFiltresSansPage);
    if (page !== 1) setPage(1);
  }

  const typesContact = useOptionsTypeContact(galerieSlug);
  const liste = useContacts(galerieSlug, filtres);
  // « Dernière demande » de la liste dense (3.3, écran 3) : l'API de liste
  // des contacts ne la porte pas (§ 14.2) ; elle vient des demandes
  // récentes, regroupées côté client — même parti pris que les colonnes
  // Artiste/Localisation d'`OeuvresPage`, qui recoupent deux listes.
  const demandesRecentes = useDemandes(galerieSlug, { tri: 'recent', parPage: 200 });
  const derniereDemandeParContact = useMemo(() => {
    const carte = new Map<string, string>();
    for (const demande of demandesRecentes.data?.demandes ?? []) {
      if (demande.contactId && !carte.has(demande.contactId)) {
        carte.set(demande.contactId, demande.recueLe);
      }
    }
    return carte;
  }, [demandesRecentes.data]);

  const aTraiterCompteur = useDemandes(galerieSlug, { etat: ['a_traiter'], parPage: 1 });
  const aTraiter = aTraiterCompteur.data?.aTraiter ?? 0;

  const contacts = liste.data?.contacts ?? [];
  const scores = useScoresProfils(
    galerieSlug,
    contacts.map((contact) => contact.id),
    voitProfils && filtres.tri !== 'score' && filtres.scoreMin === undefined,
  );
  const scoresParContact = new Map(
    scores.data?.scores.map((ligne) => [ligne.contactId, ligne.score]),
  );
  const peutCreer = galerieALaction(galerie, 'contacts:write');
  const [vue, definirVue] = useVue('contacts', compte.id, 'liste');

  function reinitialiserFiltres(): void {
    setRecherche('');
    setType('');
    setEtiquette('');
    setOrigine('');
    setConsentement('');
    setInclureArchives(false);
    setScoreMin('');
  }

  function villeDe(contact: ContactPresente): string {
    return contact.adresses[0]?.ville || '—';
  }

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <h1 className={styles.titre}>{t('contacts:titre')}</h1>
        {peutCreer && segment === 'carnet' ? (
          <Button size="sm" onClick={() => setNouveauOuvert(true)}>
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:contacts.nouveauContact.titre')}
          </Button>
        ) : null}
      </div>

      <div className={styles.segments} role="tablist" aria-label={t('contacts:titre')}>
        <button
          type="button"
          role="tab"
          aria-selected={segment === 'carnet'}
          className={`${styles.segment} ${segment === 'carnet' ? styles.segmentActif : ''}`}
          onClick={() => definirSegment('carnet')}
        >
          {t('regie:contacts.segments.carnet')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={segment === 'demandes'}
          className={`${styles.segment} ${segment === 'demandes' ? styles.segmentActif : ''}`}
          onClick={() => definirSegment('demandes')}
        >
          {t('contacts:demandes.titre')}
          {aTraiter > 0 ? <span className={styles.compteur}>{aTraiter}</span> : null}
        </button>
      </div>

      {segment === 'demandes' ? (
        <DemandesSegment galerieSlug={galerieSlug} compte={compte} />
      ) : (
        <>
          <div
            className={`${styles.filtres} ${filtresRepliables.racine}`}
            data-ouvert={filtresOuverts}
            role="search"
            aria-label={t('regie:contacts.filtres.titre')}
          >
            <div className={styles.champRecherche}>
              <Search size={16} strokeWidth={1.5} aria-hidden />
              <input
                type="search"
                className={styles.saisieRecherche}
                placeholder={t('regie:contacts.rechercheChamp')}
                aria-label={t('regie:contacts.rechercheChamp')}
                value={recherche}
                onChange={(event) => setRecherche(event.target.value)}
              />
            </div>

            <BasculeFiltres
              titre={t('regie:contacts.filtres.titre')}
              actifs={filtresActifs}
              ouvert={filtresOuverts}
              onBasculer={() => setFiltresOuverts((valeur) => !valeur)}
            />

            <div className={`${styles.filtre} ${filtresRepliables.repliable}`}>
              <span className={styles.legende} aria-hidden>
                {t('regie:contacts.filtres.type')}
              </span>
              <Select
                aria-label={t('regie:contacts.filtres.type')}
                value={type}
                onValueChange={setType}
                placeholder={t('regie:contacts.filtres.tousLesTypes')}
                options={[
                  { value: '', label: t('regie:contacts.filtres.tousLesTypes') },
                  ...typesContact.options,
                ]}
              />
            </div>

            <div className={`${styles.filtre} ${filtresRepliables.repliable}`}>
              <span className={styles.legende} aria-hidden>
                {t('regie:contacts.filtres.etiquette')}
              </span>
              <input
                type="text"
                className={styles.saisieEtiquette}
                placeholder={t('regie:contacts.filtres.etiquette')}
                aria-label={t('regie:contacts.filtres.etiquette')}
                value={etiquette}
                onChange={(event) => setEtiquette(event.target.value)}
              />
            </div>

            <div className={`${styles.filtre} ${filtresRepliables.repliable}`}>
              <span className={styles.legende} aria-hidden>
                {t('regie:contacts.filtres.origine')}
              </span>
              <Select
                aria-label={t('regie:contacts.filtres.origine')}
                value={origine}
                onValueChange={setOrigine}
                placeholder={t('regie:contacts.filtres.toutesLesOrigines')}
                options={[
                  { value: '', label: t('regie:contacts.filtres.toutesLesOrigines') },
                  ...ORIGINES_CONTACT.map((code) => ({
                    value: code,
                    label: t(`contacts:origines.${code}`),
                  })),
                ]}
              />
            </div>

            <div className={`${styles.filtre} ${filtresRepliables.repliable}`}>
              <span className={styles.legende} aria-hidden>
                {t('regie:contacts.filtres.consentement')}
              </span>
              <Select
                aria-label={t('regie:contacts.filtres.consentement')}
                value={consentement}
                onValueChange={setConsentement}
                placeholder={t('regie:contacts.filtres.consentementToutes')}
                options={[
                  { value: '', label: t('regie:contacts.filtres.consentementToutes') },
                  { value: 'true', label: t('regie:contacts.filtres.consentementOui') },
                  { value: 'false', label: t('regie:contacts.filtres.consentementNon') },
                ]}
              />
            </div>

            <div className={`${styles.filtre} ${styles.filtreTri} ${filtresRepliables.repliable}`}>
              <span className={styles.legende} aria-hidden>
                {t('regie:contacts.tri.libelle')}
              </span>
              <Select
                aria-label={t('regie:contacts.tri.libelle')}
                value={filtres.tri}
                onValueChange={(valeur) => setTri(valeur as FiltresContacts['tri'])}
                options={[
                  ...TRIS.map((code) => ({ value: code, label: t(`regie:contacts.tri.${code}`) })),
                  ...(voitProfils ? [{ value: 'score', label: t('regie:profils.triScore') }] : []),
                ]}
              />
            </div>

            {voitProfils && (
              <div className={`${styles.filtre} ${filtresRepliables.repliable}`}>
                <label className={styles.legende} htmlFor="contacts-score-min">
                  {t('regie:profils.scoreMin')}
                </label>
                <input
                  id="contacts-score-min"
                  className={styles.saisieEtiquette}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={scoreMin}
                  onChange={(event) => setScoreMin(event.target.value)}
                />
              </div>
            )}
            <div className={`${styles.filtresPied} ${filtresRepliables.repliable}`}>
              <div className={styles.caseArchives}>
                <Checkbox
                  id="contacts-archives"
                  label={t('regie:contacts.filtres.inclureArchives')}
                  checked={inclureArchives}
                  onCheckedChange={(valeur) => setInclureArchives(valeur === true)}
                />
              </div>

              <Button variant="ghost" size="sm" onClick={reinitialiserFiltres}>
                {t('regie:contacts.filtres.reinitialiser')}
              </Button>
            </div>
          </div>

          <div className={styles.barreVue}>
            <BasculeVue vue={vue} onChange={definirVue} />
          </div>

          <div className={styles.liste}>
            {voitProfils && scores.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
            {liste.isLoading ? (
              <p className={styles.vide}>{t('commun:chargement')}</p>
            ) : liste.isError ? (
              <p className={styles.vide} role="alert">
                {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
              </p>
            ) : contacts.length === 0 ? (
              <p className={styles.vide}>{t('regie:contacts.aucunResultat')}</p>
            ) : vue === 'cartes' ? (
              <ul className={styles.cartes}>
                {contacts.map((contact) => {
                  const derniereDemande = derniereDemandeParContact.get(contact.id);
                  const score =
                    contact.score === undefined ? scoresParContact.get(contact.id) : contact.score;
                  const email = contact.anonymise ? undefined : contact.emails[0];
                  const telephone = contact.anonymise ? undefined : contact.telephones[0];
                  return (
                    <li
                      key={contact.id}
                      className={styles.carte}
                      data-archive={contact.archive ? 'true' : 'false'}
                    >
                      <Link
                        className={styles.carteLien}
                        to={`/${galerieSlug}/contacts/${contact.id}`}
                      >
                        <Monogramme nom={contact.anonymise ? '' : contact.nomAffiche} taille={48} />
                        <span className={styles.identite}>
                          <span className={styles.nomContact}>
                            {contact.anonymise
                              ? t('regie:contacts.contactSupprime')
                              : contact.nomAffiche}
                          </span>
                          <span className={styles.sousLigne}>
                            {[
                              typesContact.libelle(contact.type),
                              !contact.anonymise && contact.raisonSociale
                                ? contact.raisonSociale
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </Link>
                      <dl className={styles.carteInfos}>
                        <div>
                          <dt>{t('regie:contacts.colonneVille')}</dt>
                          <dd>{villeDe(contact)}</dd>
                        </div>
                        <div>
                          <dt>{t('regie:contacts.colonneDerniereDemande')}</dt>
                          <dd>
                            {derniereDemande
                              ? formatDate(new Date(derniereDemande), langue, 'court')
                              : '—'}
                          </dd>
                        </div>
                        {voitProfils && (
                          <div>
                            <dt>{t('regie:profils.score')}</dt>
                            <dd>{score ?? '—'}</dd>
                          </div>
                        )}
                      </dl>
                      {contact.etiquettes.length > 0 && (
                        <div className={styles.etiquettes}>
                          {contact.etiquettes.map((valeur) => (
                            <span key={valeur} className={styles.etiquette}>
                              {valeur}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className={styles.cartePied}>
                        <Badge
                          tone={contact.consentementNewsletter.accorde ? 'available' : 'neutral'}
                        >
                          {contact.consentementNewsletter.accorde
                            ? t('contacts:consentement.titre')
                            : t('regie:contacts.consentementNon')}
                        </Badge>
                        <span className={styles.carteActions}>
                          {email ? (
                            <a
                              className={styles.carteAction}
                              href={`mailto:${email}`}
                              aria-label={t('regie:contacts.vue.ecrire', {
                                nom: contact.nomAffiche,
                              })}
                            >
                              <Mail size={16} strokeWidth={1.5} aria-hidden />
                            </a>
                          ) : null}
                          {telephone ? (
                            <a
                              className={styles.carteAction}
                              href={`tel:${telephone}`}
                              aria-label={t('regie:contacts.vue.appeler', {
                                nom: contact.nomAffiche,
                              })}
                            >
                              <Phone size={16} strokeWidth={1.5} aria-hidden />
                            </a>
                          ) : null}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className={styles.tableauEnveloppe}>
                <table className={styles.tableau} data-density="registre">
                  <thead>
                    <tr>
                      <th scope="col">{t('regie:contacts.colonneNom')}</th>
                      {voitProfils && <th scope="col">{t('regie:profils.score')}</th>}
                      <th scope="col">{t('regie:contacts.colonneType')}</th>
                      <th scope="col">{t('regie:contacts.colonneVille')}</th>
                      <th scope="col">{t('regie:contacts.colonneEtiquettes')}</th>
                      <th scope="col">{t('regie:contacts.colonneConsentement')}</th>
                      <th scope="col" className={styles.date}>
                        {t('regie:contacts.colonneDerniereDemande')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => {
                      const derniereDemande = derniereDemandeParContact.get(contact.id);
                      const score =
                        contact.score === undefined
                          ? scoresParContact.get(contact.id)
                          : contact.score;
                      return (
                        <tr key={contact.id} data-archive={contact.archive ? 'true' : 'false'}>
                          <td>
                            <Link
                              className={styles.ligneLien}
                              to={`/${galerieSlug}/contacts/${contact.id}`}
                            >
                              <Monogramme nom={contact.anonymise ? '' : contact.nomAffiche} />
                              <span className={styles.identite}>
                                <span className={styles.nomContact}>
                                  {contact.anonymise
                                    ? t('regie:contacts.contactSupprime')
                                    : contact.nomAffiche}
                                </span>
                                {contact.emails[0] ? (
                                  <span className={styles.sousLigne}>{contact.emails[0]}</span>
                                ) : null}
                              </span>
                            </Link>
                          </td>
                          {voitProfils && (
                            <td data-label={t('regie:profils.score')}>{score ?? '—'}</td>
                          )}
                          <td data-label={t('regie:contacts.colonneType')}>
                            {typesContact.libelle(contact.type)}
                          </td>
                          <td data-label={t('regie:contacts.colonneVille')}>{villeDe(contact)}</td>
                          <td data-label={t('regie:contacts.colonneEtiquettes')}>
                            <div className={styles.etiquettes}>
                              {contact.etiquettes.length === 0
                                ? '—'
                                : contact.etiquettes.map((valeur) => (
                                    <span key={valeur} className={styles.etiquette}>
                                      {valeur}
                                    </span>
                                  ))}
                            </div>
                          </td>
                          <td data-label={t('regie:contacts.colonneConsentement')}>
                            <Badge
                              tone={
                                contact.consentementNewsletter.accorde ? 'available' : 'neutral'
                              }
                            >
                              {contact.consentementNewsletter.accorde
                                ? t('contacts:consentement.titre')
                                : t('regie:contacts.consentementNon')}
                            </Badge>
                          </td>
                          <td
                            className={styles.date}
                            data-label={t('regie:contacts.colonneDerniereDemande')}
                          >
                            {derniereDemande
                              ? formatDate(new Date(derniereDemande), langue, 'court')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {liste.data && liste.data.pagination.pages > 1 ? (
              <div className={styles.pagination}>
                <IconButton
                  variant="outline"
                  icon={<ChevronLeft aria-hidden />}
                  aria-label={t('regie:oeuvres.pagination.precedent')}
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
                />
                <span>
                  {t('regie:oeuvres.pagination.page', {
                    page: liste.data.pagination.page,
                    pages: liste.data.pagination.pages,
                  })}
                </span>
                <IconButton
                  variant="outline"
                  icon={<ChevronRight aria-hidden />}
                  aria-label={t('regie:oeuvres.pagination.suivant')}
                  size="sm"
                  disabled={page >= liste.data.pagination.pages}
                  onClick={() =>
                    setPage((valeur) => Math.min(liste.data!.pagination.pages, valeur + 1))
                  }
                />
              </div>
            ) : null}
          </div>
        </>
      )}

      <NouveauContactDialog
        key={nouveauOuvert ? 'nouveau-ouvert' : 'nouveau-ferme'}
        galerieSlug={galerieSlug}
        open={nouveauOuvert}
        onOpenChange={setNouveauOuvert}
        onCree={(id) => {
          setNouveauOuvert(false);
          navigate(`/${galerieSlug}/contacts/${id}`);
        }}
      />
    </div>
  );
}
